import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  BsArrowDownLeft,
  BsArrowLeftRight,
  BsArrowUpRight,
  BsReceipt,
  BsRepeat,
  BsTruck,
} from "react-icons/bs";
import {
  faHome,
  faBox,
  faCog,
  faChevronDown,
  faCubes,
  faBuilding,
  faBalanceScale,
  faTag,
  faUsers,
  faLocationArrow,
  faUserGroup,
  faRecycle,
  faIndustry,
  faSignOutAlt,
  faBagShopping,
  faUpDown,
  faWarehouse,
  faHandHoldingDollar,
  faArrowsDownToLine,
  faArrowsAltV,
} from "@fortawesome/free-solid-svg-icons";
import { FiShoppingBag } from "react-icons/fi";
import { GoNote } from "react-icons/go";
import { FaShoppingCart, FaPeopleCarry, FaClipboardCheck, FaRegCreditCard, FaHandHoldingUsd } from "react-icons/fa";
import { LiaFileInvoiceDollarSolid } from "react-icons/lia";
import { useNavigate } from "react-router-dom";
import { MdOutlineCreditCard, MdOutlinePayments } from "react-icons/md";
import { RiSecurePaymentFill } from "react-icons/ri";
import { IoReceiptOutline } from "react-icons/io5";

const Sidebar = ({ onContentChange }) => {
  const [openMenus, setOpenMenus] = useState({});
  const [activeMenuItem, setActiveMenuItem] = useState("user-dashboard");
  const [userRole, setUserRole] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const role = localStorage.getItem("role");
    setUserRole(role);
  }, []);

  const toggleDropdown = (menu) => {
    // This allows closing other menus when a new one is opened, which is a better UX.
    // To allow multiple menus open at once, just toggle the specific menu.
    setOpenMenus(prev => ({
      // ...prev, // Uncomment to allow multiple open menus
      [menu]: !prev[menu],
    }));
  };

  const handleMenuItemClick = (menuItem) => {
    setActiveMenuItem(menuItem);
    onContentChange(menuItem);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    localStorage.removeItem("role");
    navigate("/login", { replace: true });
  };
  
  // A reusable component for main menu items with dropdowns
  const MainMenuItem = ({ menuKey, title, icon, children }) => (
    <div>
      <div
        className={`flex items-center justify-between rounded ${
          activeMenuItem === menuKey ? "bg-gray-700" : "hover:bg-gray-700"
        }`}
      >
        <button
          onClick={() => handleMenuItemClick(menuKey)}
          className="flex items-center py-2.5 px-4 text-left flex-grow"
        >
          {icon}
          {title}
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleDropdown(menuKey);
          }}
          className="px-3 py-2.5"
          aria-label={`Toggle ${title} submenu`}
        >
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`transition-transform duration-200 ${
              openMenus[menuKey] ? "transform rotate-180" : ""
            }`}
          />
        </button>
      </div>
      {openMenus[menuKey] && <div className="pl-8">{children}</div>}
    </div>
  );
  
  // A reusable component for sub-menu items
  const SubMenuItem = ({ menuKey, title, icon }) => (
     <button
      onClick={() => handleMenuItemClick(menuKey)}
      className={`flex items-center py-2 px-4 rounded w-full text-left ${
        activeMenuItem === menuKey
          ? "bg-gray-700 text-gray-300"
          : "hover:bg-gray-700"
      }`}
    >
      {icon}
      {title}
    </button>
  );

  return (
    <div className="w-70 bg-gray-800 text-white flex flex-col overflow-y-auto h-screen">
      <div className="p-4 text-xs font-bold bg-gray-900 flex items-center">
        Farm Management System
        <img src="/agro_farm_ims_logo.png" alt="logo" className="ml-4 h-12 w-12" />
      </div>

      <nav className="flex-grow overflow-y-auto">
        <button
          onClick={() => handleMenuItemClick("user-dashboard")}
          className={`flex items-center py-2.5 px-4 rounded w-full text-left ${
            activeMenuItem === "user-dashboard"
              ? "bg-gray-700 text-gray-300"
              : "hover:bg-gray-700"
          }`}
        >
          <FontAwesomeIcon icon={faHome} className="mr-3" />
          Home
        </button>

        {/* Master Data */}
        {(userRole === "Main Admin" || userRole === "Company Admin" || userRole === "Farm User" || userRole === "Outlet User") && (
          <MainMenuItem
            menuKey="master-data"
            title="Master Data"
            icon={<FontAwesomeIcon icon={faBox} className="mr-3" />}
          >
            {/* If Outlet User → only Customers */}
            {userRole === "Outlet User" ? (
              <>
                <SubMenuItem
                  menuKey="customers"
                  title="Customers"
                  icon={<FontAwesomeIcon icon={faUserGroup} className="mr-3" />}
                />
              </>
            ) : userRole === "Farm User" ? (
              /* If Farm User → show limited items */
              <>
                <SubMenuItem
                  menuKey="customers"
                  title="Customers"
                  icon={<FontAwesomeIcon icon={faUserGroup} className="mr-3" />}
                />
                <SubMenuItem
                  menuKey="products"
                  title="Products"
                  icon={<FontAwesomeIcon icon={faCubes} className="mr-3" />}
                />
                <SubMenuItem
                  menuKey="clusters"
                  title="Clusters"
                  icon={<FontAwesomeIcon icon={faArrowsAltV} className="mr-3" />}
                />
              </>
            ) : (
              /* For Main Admin + Company Admin */
              <>
                <SubMenuItem
                  menuKey="products"
                  title="Products"
                  icon={<FontAwesomeIcon icon={faCubes} className="mr-3" />}
                />
                <SubMenuItem
                  menuKey="customers"
                  title="Customers"
                  icon={<FontAwesomeIcon icon={faUserGroup} className="mr-3" />}
                />
                <SubMenuItem
                  menuKey="suppliers"
                  title="Suppliers"
                  icon={<FaPeopleCarry className="mr-3" />}
                />
                <SubMenuItem
                  menuKey="locations"
                  title="Locations"
                  icon={<FontAwesomeIcon icon={faLocationArrow} className="mr-3" />}
                />
                <SubMenuItem
                  menuKey="users"
                  title="Users"
                  icon={<FontAwesomeIcon icon={faUsers} className="mr-3" />}
                />
                <SubMenuItem
                  menuKey="product-categories"
                  title="Category"
                  icon={<FontAwesomeIcon icon={faTag} className="mr-3" />}
                />
                <SubMenuItem
                  menuKey="clusters"
                  title="Clusters"
                  icon={<FontAwesomeIcon icon={faArrowsAltV} className="mr-3" />}
                />
                {userRole === "Main Admin" && (
                  <>
                    <SubMenuItem
                      menuKey="units"
                      title="Units"
                      icon={<FontAwesomeIcon icon={faBalanceScale} className="mr-3" />}
                    />
                    <SubMenuItem
                      menuKey="organizations"
                      title="Organizations"
                      icon={<FontAwesomeIcon icon={faBuilding} className="mr-3" />}
                    />
                  </>
                )}
              </>
            )}
          </MainMenuItem>
        )}
        
        {/* Production */}
        {(userRole === "Main Admin" || userRole === "Company Admin" || userRole === "Farm User" || userRole === "Outlet User") && (
          <MainMenuItem
            menuKey="production"
            title="Production"
            icon={<FontAwesomeIcon icon={faIndustry} className="mr-3" />}
          >
            {/* Outlet User → only Stock Adjustment */}
            {userRole === "Outlet User" ? (
              <SubMenuItem
                menuKey="stock-adjustment"
                title="Stock Adjustment"
                icon={<FontAwesomeIcon icon={faUpDown} className="mr-3" />}
              />
            ) : (
              <>
                <SubMenuItem
                  menuKey="production-batch"
                  title="Production Batch"
                  icon={<FontAwesomeIcon icon={faRecycle} className="mr-3" />}
                />
                <SubMenuItem
                  menuKey="stock-adjustment"
                  title="Stock Adjustment"
                  icon={<FontAwesomeIcon icon={faUpDown} className="mr-3" />}
                />
              </>
            )}
          </MainMenuItem>
        )}
        
        {/* Orders */}
        {(userRole === "Main Admin" || userRole === "Company Admin" || userRole === "Farm User" || userRole === "Outlet User") && (
          <MainMenuItem
            menuKey="orders"
            title="Orders"
            icon={<FontAwesomeIcon icon={faBagShopping} className="mr-3" />}
          >
            <SubMenuItem menuKey="order-cycle" title="Order Cycle" icon={<BsRepeat className="mr-3" />} />
            <SubMenuItem menuKey="order-request" title="Order Requests" icon={<BsArrowDownLeft className="mr-3" />} />
            <SubMenuItem menuKey="sales-orders" title="Sales Orders" icon={<BsArrowUpRight className="mr-3" />} />
            <SubMenuItem menuKey="transfer-order-receipts" title="Transfer Order Receipts" icon={<BsReceipt className="mr-3" />} />
            
            {userRole !== "Main Admin" && userRole !== "Outlet User" && (
              <>
                <SubMenuItem
                  menuKey="transfer-orders"
                  title="Transfer Orders"
                  icon={<BsArrowLeftRight className="mr-3" />}
                />
              </>
            )}
            <SubMenuItem menuKey="transport-orders" title="Transport Orders" icon={<BsTruck className="mr-3" />} />
          </MainMenuItem>
        )}

        {/* Stock On Loan */}
        {(userRole === "Main Admin" || userRole === "Company Admin" || userRole === "Farm User" || userRole === "Outlet User") && (
            <MainMenuItem menuKey="stock-on-loan" title="Stock on Loan" icon={<FontAwesomeIcon icon={faHandHoldingDollar} className="mr-3" />}>
                <SubMenuItem menuKey="dispatched-stock-on-loan" title="Dispatched" icon={<FaClipboardCheck className="mr-3" />} />
                <SubMenuItem menuKey="incoming-stock-on-loan" title="Incoming" icon={<FontAwesomeIcon icon={faArrowsDownToLine} className="mr-3" />} />
            </MainMenuItem>
        )}

        {/* Sales */}
        {(userRole === "Company Admin" || userRole === "Outlet User" || userRole === "Farm User" || userRole === "Accountant") && (
            <MainMenuItem menuKey="invoices" title="Sales" icon={<RiSecurePaymentFill className="mr-3" />}>
                <SubMenuItem menuKey="sales-invoices" title="Sales Invoice" icon={<LiaFileInvoiceDollarSolid className="mr-3" />} />
                <SubMenuItem menuKey="credit-note" title="Credit Notes" icon={<MdOutlineCreditCard className="mr-3" />} />
                <SubMenuItem menuKey="payment" title="Receipts" icon={<MdOutlinePayments className="mr-3" />} />
                <SubMenuItem menuKey="settelments" title="Settlements" icon={<IoReceiptOutline className="mr-3" />} />
            </MainMenuItem>
        )}

        {/* Procurement */}
        {(userRole === "Main Admin" || userRole === "Company Admin" || userRole === "Outlet User" || userRole === "Accountant" || userRole === "Farm User") && (
            <MainMenuItem menuKey="purchasing" title="Procurement" icon={<FaShoppingCart className="mr-3" />}>
                <SubMenuItem menuKey="purchase-orders" title="Purchase Order" icon={<FiShoppingBag className="mr-3" />} />
                <SubMenuItem menuKey="goods-receipt-note" title="Good Receipt" icon={<GoNote className="mr-3" />} />
                <SubMenuItem menuKey="purchase-invoices" title="Purchasing Invoice" icon={<LiaFileInvoiceDollarSolid className="mr-3" />} />
                <SubMenuItem menuKey="SupplierPayments" title="Supplier Payments" icon={<FaHandHoldingUsd className="mr-3" />} />
                <SubMenuItem menuKey="debit-note" title="Debit Notes" icon={<FaRegCreditCard  className="mr-3" />} />
                <SubMenuItem menuKey="PurchaseOrderSettlements" title="Settlements" icon={<IoReceiptOutline className="mr-3" />} />
            </MainMenuItem>
        )}
        
        {/* Reports */}
        {(userRole === "Main Admin" || userRole === "Company Admin" || userRole === "Farm User" || userRole === "Outlet User") && (
          <MainMenuItem menuKey="reports" title="Reports" icon={<FontAwesomeIcon icon={faIndustry} className="mr-3" />}>
            <SubMenuItem menuKey="stock-view" title="Stock" icon={<FontAwesomeIcon icon={faWarehouse} className="mr-3" />} />
            <SubMenuItem menuKey="stock-movement" title="Stock Movement" icon={<FontAwesomeIcon icon={faArrowsAltV} className="mr-3" />} />
          </MainMenuItem>
        )}

        {/* Settings */}
        {(userRole === "Main Admin" || userRole === "Company Admin") && (
          <button
            onClick={() => handleMenuItemClick("settings")}
            className={`flex items-center py-2.5 px-4 rounded w-full text-left ${
              activeMenuItem === "settings"
                ? "bg-gray-700 text-gray-300"
                : "hover:bg-gray-700"
            }`}
          >
            <FontAwesomeIcon icon={faCog} className="mr-3" />
            Settings
          </button>
        )}
      </nav>

      <div className="py-2 bg-gray-900 flex items-center">
        <button
          onClick={handleLogout}
          className="flex items-center py-2 px-4 w-full bg-gray-900 text-left hover:bg-gray-950 focus:outline-none"
        >
          <FontAwesomeIcon icon={faSignOutAlt} className="mr-3" />
          Logout
        </button>
      </div>
    </div>
  );
};

export default Sidebar;